import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 18672:226415 — InputGroup):
 *
 * Container: rounded-lg (10px), border border-input, bg-background
 * Input type:  h-9 (36px), px-2.5 (10px), py-2 (8px), gap-1.5 (6px), flex items-center
 * Textarea type: flex-col, min-h-[104px], no fixed height
 *
 * States:
 *   Default:  border-input
 *   Focus:    border-ring, ring-1 ring-ring/50 (via focus-within)
 *   Disabled: opacity-50
 *   Invalid:  border-destructive, ring-destructive/20
 *
 * Addon Inline (node 18677:9902): icon, text, kbd, button, spinner, tooltip, dropdown, check
 *   Icon:  size-4 text-muted-foreground
 *   Text:  text-sm font-medium text-muted-foreground
 *
 * Addon Block (node 18677:10743): full-width row, px-2.5 py-2, border-t or border-b
 */

export interface IntuiInputGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Render with destructive border and focus ring */
  invalid?: boolean
  /** Render as disabled (opacity-50) */
  disabled?: boolean
  /** Inline content rendered before the input */
  startAddon?: React.ReactNode
  /** Inline content rendered after the input */
  endAddon?: React.ReactNode
  /** Full-width block above the input area (textarea use case) */
  blockStart?: React.ReactNode
  /** Full-width block below the input area (textarea use case) */
  blockEnd?: React.ReactNode
  children: React.ReactNode
}

const DisabledContext = React.createContext(false)

/** Hook to read the disabled state from a parent IntuiInputGroup */
function useInputGroupDisabled() {
  return React.useContext(DisabledContext)
}

const IntuiInputGroup = React.forwardRef<HTMLDivElement, IntuiInputGroupProps>(
  (
    {
      className,
      invalid,
      disabled,
      startAddon,
      endAddon,
      blockStart,
      blockEnd,
      children,
      ...props
    },
    ref,
  ) => {
    const hasBlocks = blockStart || blockEnd

    const inlineContent = (
      <>
        {startAddon && (
          <div className="flex items-center gap-1.5 shrink-0 [&>svg]:size-4 [&>svg]:text-muted-foreground">
            {startAddon}
          </div>
        )}
        {children}
        {endAddon && (
          <div className="flex items-center gap-1.5 shrink-0 [&>svg]:size-4 [&>svg]:text-muted-foreground">
            {endAddon}
          </div>
        )}
      </>
    )

    return (
      <DisabledContext.Provider value={!!disabled}>
        <div
          ref={ref}
          className={cn(
            'rounded-lg border border-input bg-background text-sm transition-colors',
            'focus-within:ring-1 focus-within:ring-ring focus-within:border-ring',
            invalid && 'border-destructive focus-within:ring-destructive/20 focus-within:border-destructive',
            disabled && 'opacity-50 cursor-not-allowed [&_input]:pointer-events-none [&_textarea]:pointer-events-none [&_button]:pointer-events-none',
            !hasBlocks && 'flex items-center gap-1.5 px-2.5 py-2 h-9',
            hasBlocks && 'flex flex-col',
            className,
          )}
          aria-invalid={invalid || undefined}
          aria-disabled={disabled || undefined}
          {...props}
        >
          {blockStart && (
            <div className="flex items-center justify-between px-2.5 py-2 border-b border-input">
              {blockStart}
            </div>
          )}
          {hasBlocks ? (
            <div className="flex items-center gap-1.5 px-2.5 py-2 flex-1">
              {inlineContent}
            </div>
          ) : (
            inlineContent
          )}
          {blockEnd && (
            <div className="flex items-center justify-between px-2.5 py-2 border-t border-input">
              {blockEnd}
            </div>
          )}
        </div>
      </DisabledContext.Provider>
    )
  },
)
IntuiInputGroup.displayName = 'IntuiInputGroup'

/** Unstyled input for use inside IntuiInputGroup (strips border/ring/shadow from base) */
const IntuiInputGroupInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, disabled: disabledProp, ...props }, ref) => {
  const groupDisabled = useInputGroupDisabled()
  return (
    <input
      ref={ref}
      disabled={disabledProp ?? groupDisabled}
      className={cn(
        'flex-1 min-w-0 bg-transparent text-sm placeholder:text-muted-foreground',
        'focus-visible:outline-none disabled:cursor-not-allowed',
        className,
      )}
      {...props}
    />
  )
})
IntuiInputGroupInput.displayName = 'IntuiInputGroupInput'

/** Unstyled textarea for use inside IntuiInputGroup */
const IntuiInputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<'textarea'>
>(({ className, disabled: disabledProp, ...props }, ref) => {
  const groupDisabled = useInputGroupDisabled()
  return (
    <textarea
      ref={ref}
      disabled={disabledProp ?? groupDisabled}
      className={cn(
        'flex-1 min-w-0 min-h-[64px] bg-transparent text-sm placeholder:text-muted-foreground resize-none',
        'focus-visible:outline-none disabled:cursor-not-allowed',
        className,
      )}
      {...props}
    />
  )
})
IntuiInputGroupTextarea.displayName = 'IntuiInputGroupTextarea'

/** Addon text label (e.g., "https://", ".com", "USD") */
function IntuiInputGroupText({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('text-sm font-medium text-muted-foreground whitespace-nowrap', className)}>
      {children}
    </span>
  )
}

export {
  IntuiInputGroup,
  IntuiInputGroupInput,
  IntuiInputGroupTextarea,
  IntuiInputGroupText,
}
